import { inject, Injectable } from '@angular/core';
import { UploadGuard } from '@app/guards/upload-guard';
import { DialogFlowHelper } from '@common/modules/dialog/dialog-flow.helper';
import { ActionDisabledCondition, ActionItemOptions, UserAction } from '@mediahaven/shared';
import {
    ApiFragment,
    Family,
    Mh2Entity,
    NavigationLink,
    RecordStatus,
    RecordTypesService
} from '@stylehaven/components';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ActionService } from '../../abstract/action.service.interface';
import { MediaHavenActionType } from '@common/sandboxes/actions/entities/action-types.entity';

@Injectable({ providedIn: 'root' })
export class AddObjectsActionService extends ActionService {
    protected override readonly action: MediaHavenActionType = MediaHavenActionType.ADD_OBJECTS;

    // Services
    private readonly recordTypeService: RecordTypesService = inject(RecordTypesService);
    private readonly uploadGuard: UploadGuard = inject(UploadGuard);
    private readonly flow: DialogFlowHelper = inject(DialogFlowHelper);

    // Behaviour
    protected readonly ENABLE_BUSY_TOAST: boolean = false;
    protected readonly REFRESH_ON_ACTION: boolean = true;
    protected readonly DISABLE_ON_EXECUTE: boolean = false;
    protected readonly ENABLE_FINISHED_TOASTS: boolean = false;

    /* Add object
     * Available when:
     *  * Object can have children
     *  * User can upload
     *  * User has edit rights
     *  * It is NOT deleted
     *  * In context of a parent
     *
     * Disabled when:
     *  * Has non-editable status
     *  * Object is Submitted --> Override for evaluators which can edit Submitted files
     */
    public getUserAction(target: ApiFragment, actionItemOptions: ActionItemOptions): Observable<UserAction> {
        return this.uploadGuard.hasAccess().pipe(
            map(canUpload => {
                return this.userActionBuilder(target, actionItemOptions, canUpload);
            })
        );
    }

    public userActionBuilder(
        record: ApiFragment,
        actionItemOptions: ActionItemOptions,
        canUpload: boolean
    ): UserAction {
        const canHaveChildren: boolean = this.recordTypeService.getAllowedChildren(record.getRecordType()).length > 0;
        const hasEditRights: boolean = record.hasEditRights();
        const isDeleted: boolean = record.isDeleted();
        const isInParentRecordContext: boolean = record === actionItemOptions.contextFragment;

        const hasEditableStatus: boolean = record.get(Family.Context, 'HasEditableStatus');
        const isSubmitted: boolean = record.getRecordStatus() === RecordStatus.Submitted;

        const availableConditions: boolean[] = [
            canUpload,
            hasEditRights,
            canHaveChildren,
            !isDeleted,
            isInParentRecordContext
        ];
        const disableConditions: ActionDisabledCondition[] = [
            { condition: !hasEditableStatus, tooltip: 'general.blocked-status' },
            { condition: isSubmitted, tooltip: 'general.blocked-status' }
        ];

        return new UserAction(availableConditions, disableConditions);
    }

    protected onAction(record: ApiFragment, actionItemOptions: ActionItemOptions): Observable<boolean> {
        // Don't navigate to parent if action was started from detail page
        return this.flow.childFlow({ parent: record, navigateOnFinish: !actionItemOptions.contextFragment });
    }

    public getLabelKey(target: ApiFragment): string {
        return 'actions.add-objects';
    }

    public getToastLabelKey(target: ApiFragment): string {
        return '';
    }

    public getToastEntity(target: ApiFragment, actionItemOptions: ActionItemOptions): Mh2Entity | string {
        return Mh2Entity.Generic;
    }

    public getSuccessToastLinkLabelKey(target: ApiFragment, actionItemOptions: ActionItemOptions): string {
        return '';
    }

    public getSuccessToastLink(target: ApiFragment, actionItemOptions: ActionItemOptions): NavigationLink | undefined {
        return undefined;
    }

    public getToastTranslationOptions(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): Record<string, string> {
        return {};
    }
}
